import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Map } from 'lucide-react';

const MazeGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);
  const [maze, setMaze] = useState([]);
  const [playerPos, setPlayerPos] = useState({ x: 1, y: 1 });
  const [moves, setMoves] = useState(0);
  
  const mazeSize = 15;

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setMoves(0);
    generateMaze();
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setMoves(0);
    setMaze([]);
    setPlayerPos({ x: 1, y: 1 });
  };

  const generateMaze = () => {
    const newMaze = Array(mazeSize).fill().map(() => Array(mazeSize).fill(1)); // 1 = wall
    
    // Create paths using recursive backtracking
    const stack = [{ x: 1, y: 1 }];
    newMaze[1][1] = 0; // 0 = path
    
    while (stack.length > 0) {
      const current = stack[stack.length - 1];
      const neighbors = getUnvisitedNeighbors(current, newMaze);
      
      if (neighbors.length === 0) {
        stack.pop();
      } else {
        const next = neighbors[Math.floor(Math.random() * neighbors.length)];
        const wall = {
          x: current.x + (next.x - current.x) / 2,
          y: current.y + (next.y - current.y) / 2
        };
        
        newMaze[wall.y][wall.x] = 0;
        newMaze[next.y][next.x] = 0;
        stack.push(next);
      }
    }
    
    // Ensure exit is accessible
    newMaze[mazeSize - 2][mazeSize - 2] = 0;
    
    setMaze(newMaze);
    setPlayerPos({ x: 1, y: 1 });
  };

  const getUnvisitedNeighbors = (pos, maze) => {
    const neighbors = [];
    const directions = [
      { x: 0, y: -2 }, { x: 2, y: 0 }, { x: 0, y: 2 }, { x: -2, y: 0 }
    ];
    
    directions.forEach(dir => {
      const newX = pos.x + dir.x;
      const newY = pos.y + dir.y;
      
      if (newX > 0 && newX < mazeSize - 1 && newY > 0 && newY < mazeSize - 1) {
        if (maze[newY][newX] === 1) {
          neighbors.push({ x: newX, y: newY });
        }
      }
    });
    
    return neighbors;
  };

  const movePlayer = (dx, dy) => {
    if (!isPlaying || gameOver) return;
    
    const newX = playerPos.x + dx;
    const newY = playerPos.y + dy;
    
    if (newX >= 0 && newX < mazeSize && newY >= 0 && newY < mazeSize) {
      if (maze[newY][newX] === 0) {
        setPlayerPos({ x: newX, y: newY });
        setMoves(prev => prev + 1);
        
        // Check if reached exit
        if (newX === mazeSize - 2 && newY === mazeSize - 2) {
          const levelScore = Math.max(1000 - moves * 10, 100);
          setScore(prev => prev + levelScore);
          
          if (level < 5) {
            setLevel(prev => prev + 1);
            setTimeout(() => {
              generateMaze();
              setMoves(0);
            }, 1000);
          } else {
            setGameOver(true);
            setIsPlaying(false);
            if (score + levelScore > highScore) {
              setHighScore(score + levelScore);
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isPlaying || gameOver) return;

      switch (e.code) {
        case 'ArrowUp':
          e.preventDefault();
          movePlayer(0, -1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          movePlayer(0, 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          movePlayer(-1, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          movePlayer(1, 0);
          break;
        case 'Space':
          e.preventDefault();
          if (!isPlaying) {
            startGame();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, gameOver, playerPos]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Map className="h-6 w-6 text-neon-purple" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-purple">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Cyber Maze
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Navigate through the maze to reach the exit. Use arrow keys to move!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && !gameOver && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Level {level} - Find the Exit!
            </h4>
            
            {/* Maze Display */}
            <div className="flex justify-center mb-6">
              <div className="grid gap-1 bg-dark-200 p-4 rounded-lg">
                {maze.map((row, y) =>
                  row.map((cell, x) => (
                    <div
                      key={`${x}-${y}`}
                      className={`w-6 h-6 rounded-sm ${
                        cell === 1 
                          ? 'bg-gray-700' 
                          : x === playerPos.x && y === playerPos.y
                            ? 'bg-neon-blue animate-pulse'
                            : x === mazeSize - 2 && y === mazeSize - 2
                              ? 'bg-neon-green animate-pulse'
                              : 'bg-dark-100'
                      }`}
                    >
                      {x === playerPos.x && y === playerPos.y && (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold">
                          P
                        </div>
                      )}
                      {x === mazeSize - 2 && y === mazeSize - 2 && x !== playerPos.x && y !== playerPos.y && (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold">
                          E
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Game Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
              <div>
                <p className="font-bold text-neon-blue">Moves:</p>
                <p className="text-xl font-cyber font-bold text-neon-blue">{moves}</p>
              </div>
              <div>
                <p className="font-bold text-neon-green">Target:</p>
                <p className="text-xl font-cyber font-bold text-neon-green">Exit</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Game Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">Level</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{level}/5</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Moves</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">{moves}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Time</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            Maze Complete! 🎯
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Total Moves: {moves} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-green text-neon-green hover:bg-neon-green hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="cyber-card p-6">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            How to Play
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue mb-2">Objective</p>
              <p>Navigate from start (P) to exit (E)</p>
            </div>
            <div>
              <p className="font-bold text-neon-green mb-2">Controls</p>
              <p>Use arrow keys to move</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink mb-2">Scoring</p>
              <p>Fewer moves = higher score</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow mb-2">Progression</p>
              <p>Complete 5 levels to win</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MazeGame;
